import { FEATURE_ALLOW_EDIT_UNFULFILLED_SALES } from '../../../../../../constants/wr-features'
import {
  computeFulfilmentTypeRestrictions,
  isNewEditableItem,
} from '../../../../../../functions/sale'
import { FulfilmentType } from '../../../../../../models/fulfilment'
import { useAppSelector } from '../../../../../../state/hooks'
import {
  selectCurrentSale,
  selectFulfilmentDetails,
  selectLineItem,
  selectSaleAttributesHasFulfilment,
  selectSaleId,
} from '../../../../../../state/slices/currentSale/currentSaleSlice'
import { selectOpenFulfilmentPopoverLineItemId } from '../../../../../../state/slices/uiStateSlice'
import { getFeatureFromCache } from '../../../../../../util/feature-cache'

type UseNewEditableContinuedItemFulfilmentResult = {
  /**
   * True when the line item whose fulfilment popover is currently open is a
   * new (no persisted id) item added while continuing an editable fulfilment
   * sale, with the edit-unfulfilled-sales feature enabled.
   */
  isNewEditableItem: boolean
  /**
   * When set, restricts the selectable fulfilment types for the open item to
   * the sale's existing fulfilment type and None (Take Now). Switching a
   * pickup sale to delivery (or vice versa) is never allowed. `undefined`
   * means no restriction applies.
   */
  allowedFulfilmentTypes: FulfilmentType[] | undefined
  /**
   * True when the fulfilment note must be read-only. The note is shared per
   * fulfilment type and is only editable on original sale creation, so it stays
   * read-only for the whole continued fulfilment sale — including new editable
   * items (Story AC 1.a.iiii).
   */
  isNoteDisabled: boolean
}

/**
 * Computes fulfilment-type restrictions for the line item whose fulfilment
 * popover is currently open. New items added while continuing a fulfilment
 * sale may only take the sale's existing type or None (Take Now); the other
 * fulfilment type must not be selectable in the OOS / fulfilment popover flow.
 */
export const useNewEditableContinuedItemFulfilment = (): UseNewEditableContinuedItemFulfilmentResult => {
  const lineItemId = useAppSelector(selectOpenFulfilmentPopoverLineItemId)
  const lineItem = useAppSelector(selectLineItem(lineItemId))
  const fulfilmentDetails = useAppSelector(selectFulfilmentDetails)
  const currentSale = useAppSelector(selectCurrentSale)
  const saleId = useAppSelector(selectSaleId)
  const hasFulfilment = useAppSelector(selectSaleAttributesHasFulfilment)
  const saleFulfilmentType = fulfilmentDetails[0]?.type

  const isFeatureEnabled = getFeatureFromCache(
    FEATURE_ALLOW_EDIT_UNFULFILLED_SALES
  )

  const isNewEditable =
    isFeatureEnabled && isNewEditableItem(lineItem, currentSale)

  const allowedFulfilmentTypes = isFeatureEnabled
    ? computeFulfilmentTypeRestrictions(
        lineItem,
        currentSale,
        saleFulfilmentType
      )
    : undefined

  // The fulfilment note is shared per fulfilment type and is only editable on
  // original sale creation, so it stays read-only for the whole continued
  // fulfilment sale — including new editable items (Story AC 1.a.iiii). This
  // mirrors the `isNoteDisabled` rule in useSaleFulfilment for the inline row.
  const isNoteDisabled = Boolean(saleId) && hasFulfilment

  return {
    isNewEditableItem: isNewEditable,
    allowedFulfilmentTypes,
    isNoteDisabled,
  }
}
